import { useKindeBrowserClientContext } from '@/context/kinde-browser-client-context.utils';
import { useKindeAuthIfProvided } from '@/hooks/useKindeReactAuth';
import { isTauri } from '@/tauri/utils';
import {
  readDesktopKindeAccessToken,
  type KindeDesktopTokenOptions,
} from '@/utils/auth/kindeDesktopAccessToken';
import { type KindeContextProps } from '@kinde-oss/kinde-auth-react';

const IS_TAURI = isTauri();

type HeidiKindeUser = {
  email: string | null;
  family_name?: string | null;
  given_name?: string | null;
  id?: string | null;
  picture?: string | null;
} | null;

export type HeidiKindeGetTokenOptions = KindeDesktopTokenOptions;

type HeidiKindeAuth = {
  getToken: (
    options?: HeidiKindeGetTokenOptions
  ) => Promise<string | null | undefined> | string | null;
  getUser: () => HeidiKindeUser;
  isAuthenticated: boolean | null;
  isLoading: boolean | null;
  user?: HeidiKindeUser;
  login?: KindeContextProps['login'];
  logout?: KindeContextProps['logout'];
  register?: KindeContextProps['register'];
};

const unauthenticatedKindeAuth = {
  getToken: () => null,
  getUser: () => null,
  isAuthenticated: false,
  isLoading: false,
  user: null,
} satisfies HeidiKindeAuth;

/**
 * Desktop shape when `useKinde` renders outside `KindeProvider`. It mirrors
 * v4's default context (signed out, still loading): the provider lives in
 * `TauriAuth`, but `DictationProvider` and the dev tools mount above
 * `AuthWrapper` and read auth state from here. `getToken` returns null, the
 * bridge contract for "SDK not ready". One module-level object, so consumers
 * see a stable reference.
 */
const kindeProviderUnavailable = {
  getToken: () => null,
  getUser: () => null,
  isAuthenticated: false,
  isLoading: true,
  user: null,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
} satisfies HeidiKindeAuth;

type SdkUser =
  | {
      id?: string | null;
      email?: string | null;
      givenName?: string | null;
      given_name?: string | null;
      familyName?: string | null;
      family_name?: string | null;
      picture?: string | null;
    }
  | null
  | undefined;

function toHeidiKindeUser(user: SdkUser): HeidiKindeUser {
  if (!user) return null;
  return {
    id: user.id ?? null,
    email: user.email ?? null,
    given_name: user.given_name ?? user.givenName ?? null,
    family_name: user.family_name ?? user.familyName ?? null,
    picture: user.picture ?? null,
  };
}

/**
 * Desktop hook, selected once at module scope (`IS_TAURI` does not change for
 * the life of the page). It can render outside `KindeProvider`:
 * `DictationProvider` and the dev tools mount above `AuthWrapper`, while the
 * provider lives in `TauriAuth`. v5 `useKindeAuth` throws there, where v4
 * returned a signed-out, loading default, so only that missing-provider error
 * maps to `kindeProviderUnavailable`. Any other error propagates.
 */
function useKindeTauri(): HeidiKindeAuth {
  const reactKinde = useKindeAuthIfProvided();
  if (reactKinde === null) {
    return kindeProviderUnavailable;
  }
  const user = toHeidiKindeUser(reactKinde.user);

  return {
    isAuthenticated: reactKinde.isAuthenticated,
    isLoading: reactKinde.isLoading,
    user,
    getUser: () => user,
    login: reactKinde.login,
    logout: reactKinde.logout,
    register: reactKinde.register,
    // This arrow is a fresh reference each render. `KindeDesktopBridge`
    // captures the latest one via a ref and a mount-only effect.
    getToken: (options) => readDesktopKindeAccessToken(reactKinde, options),
  };
}

/** Web hook. There is no `KindeProvider` on web, so this never calls the React SDK. */
function useKindeWeb(): HeidiKindeAuth {
  const nextKinde = useKindeBrowserClientContext();
  if (nextKinde !== null) {
    return nextKinde;
  }
  return unauthenticatedKindeAuth;
}

export const useKinde = IS_TAURI ? useKindeTauri : useKindeWeb;
